import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Card, CardContent } from '../components/ui/card';
import { getEstadoNome } from '../lib/utils';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, MessageCircle, ChevronsUpDown, Check } from 'lucide-react';

const WHATSAPP_NUMBER = '5531973381052';

// Dispara conversão do Google Ads e, depois, redireciona para a URL passada.
// Usa event_callback + timeout de segurança (2s) caso o gtag não tenha carregado.
const gtagReportConversion = (url) => {
  const go = () => {
    if (url) window.location.href = url;
  };
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    go();
    return;
  }
  let called = false;
  const cb = () => {
    if (called) return;
    called = true;
    go();
  };
  window.gtag('event', 'conversion', {
    send_to: 'AW-18400966717/QPJzcMKK9-QcEL3wocZE',
    event_callback: cb,
  });
  // fallback: se o gtag demorar demais, redireciona mesmo assim
  setTimeout(cb, 2000);
};

// Combobox pesquisável (Popover + Command)
const SearchCombobox = ({ value, onChange, options, placeholder, searchPlaceholder, emptyText, disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className={selected ? '' : 'text-gray-500'}>{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  data-testid={`${testId}-option-${o.value}`}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === o.value ? 'opacity-100' : 'opacity-0'}`} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const PublicOrcamento = () => {
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    telefone: '',
    estado: '',
    cidade: '',
    zona: '',
    descricao: '',
    metragem: '',
  });

  const estadosOptions = useMemo(() => {
    const ufs = [...new Set(cidades.map((c) => c.estado || 'MG'))].sort();
    return ufs.map((uf) => ({ value: uf, label: `${getEstadoNome(uf)} (${uf})` }));
  }, [cidades]);

  const cidadesDoEstado = useMemo(
    () => cidades.filter((c) => (c.estado || 'MG') === form.estado),
    [cidades, form.estado]
  );

  const cidadesOptions = useMemo(
    () => cidadesDoEstado.map((c) => ({ value: c.nome, label: c.nome })),
    [cidadesDoEstado]
  );

  // Zonas da cidade selecionada (só aparece se a cidade tiver zonas cadastradas)
  const zonasOptions = useMemo(() => {
    const cid = cidadesDoEstado.find((c) => c.nome === form.cidade);
    return (cid?.zonas || []).map((z) => ({ value: z.nome, label: z.nome }));
  }, [cidadesDoEstado, form.cidade]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/api/public/cidades-atendimento');
        setCidades(r.data);
      } catch (err) {
        console.error('Erro ao carregar cidades', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || form.nome.trim().length < 2) {
      toast.error('Informe seu nome completo');
      return;
    }
    if (!form.telefone.trim()) {
      toast.error('Informe seu telefone/WhatsApp');
      return;
    }
    if (!form.estado) {
      toast.error('Selecione o estado');
      return;
    }
    if (!form.cidade) {
      toast.error('Selecione a cidade');
      return;
    }
    if (zonasOptions.length > 0 && !form.zona) {
      toast.error('Selecione a zona/região');
      return;
    }
    if (!form.descricao.trim() || form.descricao.trim().length < 5) {
      toast.error('Descreva o serviço que você precisa');
      return;
    }

    setSubmitting(true);

    const zonaLabel = form.zona || '';
    const regiaoLinha = zonaLabel ? `\nZona/Região: ${zonaLabel}` : '';
    const metragemTxt = form.metragem && Number(form.metragem) > 0 ? String(form.metragem).replace('.', ',') : '';
    const metragemLinha = metragemTxt ? `\nMetragem: ${metragemTxt} m²` : '';

    // 1) Salva o lead no CRM (falha silenciosa não bloqueia o WhatsApp)
    try {
      await axios.post('/api/public/leads', {
        nome: form.nome.trim(),
        telefone: form.telefone.trim(),
        estado: form.estado,
        cidade: form.cidade,
        zona: zonaLabel,
        observacoes: `Estado: ${getEstadoNome(form.estado)} (${form.estado})\nCidade: ${form.cidade}${regiaoLinha}\nServiço solicitado: ${form.descricao.trim()}${metragemLinha}`,
      });
    } catch (err) {
      console.error('Erro ao salvar lead (seguindo para o WhatsApp mesmo assim):', err);
    }

    // 2) Monta a mensagem e abre o WhatsApp
    const mensagem =
      `Olá, gostaria de solicitar um orçamento.\n\n` +
      `*Nome:* ${form.nome.trim()}\n` +
      `*Estado:* ${getEstadoNome(form.estado)} (${form.estado})\n` +
      `*Cidade:* ${form.cidade}\n` +
      (zonaLabel ? `*Zona/Região:* ${zonaLabel}\n` : '') +
      `*Serviço:* ${form.descricao.trim()}\n` +
      (metragemTxt ? `*Metragem:* ${metragemTxt} m²\n` : '') +
      `\nAguardo retorno!`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensagem)}`;
    // 3) Dispara conversão do Google Ads e só depois redireciona para o WhatsApp
    gtagReportConversion(url);
    // fallback UI: se o navegador bloquear o redirect (raro), libera o botão em 3s
    setTimeout(() => setSubmitting(false), 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Voltar
          </Link>
          <span className="ml-auto font-semibold" style={{ fontFamily: 'Work Sans', color: '#2B7A3D' }}>
            Tenance
          </span>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Work Sans' }}>
            Solicite seu orçamento
          </h1>
          <p className="text-gray-600 mt-2">
            Preencha os dados abaixo e ao clicar em <b>Solicitar orçamento</b> você será direcionado para
            nosso WhatsApp com sua mensagem pronta.
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-orcamento-publico">
              <div>
                <Label htmlFor="nome">Nome completo *</Label>
                <Input
                  id="nome"
                  placeholder="Seu nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  disabled={submitting}
                  data-testid="input-nome"
                />
              </div>

              <div>
                <Label htmlFor="telefone">Telefone / WhatsApp *</Label>
                <Input
                  id="telefone"
                  placeholder="(31) 99999-9999"
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  disabled={submitting}
                  data-testid="input-telefone"
                />
              </div>

              <div>
                <Label>Estado *</Label>
                <SearchCombobox
                  value={form.estado}
                  onChange={(v) => setForm({ ...form, estado: v, cidade: '', zona: '' })}
                  options={estadosOptions}
                  placeholder={loading ? 'Carregando...' : 'Digite ou selecione seu estado'}
                  searchPlaceholder="Pesquisar estado..."
                  emptyText="Nenhum estado encontrado."
                  disabled={submitting || loading}
                  testId="select-estado"
                />
              </div>

              <div>
                <Label>Cidade *</Label>
                <SearchCombobox
                  value={form.cidade}
                  onChange={(v) => setForm({ ...form, cidade: v, zona: '' })}
                  options={cidadesOptions}
                  placeholder={!form.estado ? 'Selecione o estado primeiro' : 'Digite ou selecione sua cidade'}
                  searchPlaceholder="Pesquisar cidade..."
                  emptyText="Nenhuma cidade encontrada."
                  disabled={submitting || loading || !form.estado}
                  testId="select-cidade"
                />
              </div>

              {zonasOptions.length > 0 && (
                <div>
                  <Label>Zona / Região *</Label>
                  <SearchCombobox
                    value={form.zona}
                    onChange={(v) => setForm({ ...form, zona: v })}
                    options={zonasOptions}
                    placeholder="Digite ou selecione sua zona/região"
                    searchPlaceholder="Pesquisar zona..."
                    emptyText="Nenhuma zona encontrada."
                    disabled={submitting}
                    testId="select-zona"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="descricao">Descreva o serviço que você precisa *</Label>
                <Textarea
                  id="descricao"
                  rows={4}
                  placeholder="Ex: Preciso instalar 3 tomadas na cozinha e trocar um chuveiro..."
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  disabled={submitting}
                  data-testid="input-descricao"
                />
              </div>

              <div>
                <Label htmlFor="metragem">Metragem aproximada (m²) — se aplicável</Label>
                <Input
                  id="metragem"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Ex: 45 (para serviços cobrados por m², como limpeza ou pintura)"
                  value={form.metragem}
                  onChange={(e) => setForm({ ...form, metragem: e.target.value })}
                  disabled={submitting}
                  data-testid="input-metragem"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full text-white text-base py-6"
                style={{ backgroundColor: '#2B7A3D' }}
                data-testid="btn-submit-orcamento"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Abrindo WhatsApp...
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Solicitar orçamento
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                Ao enviar, você será redirecionado para o WhatsApp da Tenance com sua mensagem pronta.
              </p>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PublicOrcamento;
